import { MouseEvent, forwardRef } from 'react'
import { NavLink } from 'react-router-dom'
import { FOCUS_VISIBLE_RING, FOCUS_VISIBLE_RING_OFFSET } from '../../../lib/a11yFocus'

import { FORM_FIELD_HEIGHT_CLASS } from '../../../lib/formStyles'
import useIsViewOnly, { VIEW_ONLY_MESSAGE } from '../../../hooks/useIsViewOnly'

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'
type ButtonType =
	| 'Primary'
	| 'Secondary'
	| 'White'
	| 'on dark'
	| 'Green-Secondary'
	| 'Outlined'
	| 'Link'
	| 'Pagination'

type ButtonProps = {
	type: ButtonType
	text?: string
	onClick?: (event: MouseEvent) => void
	disabled?: boolean
	isRounded?: boolean
	hasLeadingIcon?: boolean
	hasTrailingIcon?: boolean
	icon?: string
	size?: ButtonSize
	isLink?: boolean
	url?: string
	fullWidth?: boolean
	additionalClass?: string
	ariaControls?: string
	ariaExpanded?: boolean
	buttonType?: 'button' | 'submit'
	accessibilityLabel?: string
	routeState?: string
	id?: string
	isTableRowButton?: boolean
	/** Sizing context: 'form-field' matches the height of Input/SearchableSelect on the same row; 'table-row' is the same as isTableRowButton. */
	context?: 'default' | 'form-field' | 'table-row'
	/** Makes a change in the organisation: rendered disabled (never a link) for view-only access. */
	requiresWrite?: boolean
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
	{
		type,
		text = '',
		onClick,
		disabled: disabledProp = false,
		isRounded,
		hasLeadingIcon,
		hasTrailingIcon,
		icon,
		size = 'md',
		isLink = false,
		url = '/',
		additionalClass,
		fullWidth,
		ariaControls = undefined,
		ariaExpanded = undefined,
		buttonType = 'button',
		accessibilityLabel,
		routeState = undefined,
		id = undefined,
		isTableRowButton = false,
		context = 'default',
		requiresWrite = false,
	},
	ref
) {
	const isViewOnly = useIsViewOnly()
	const isBlocked = requiresWrite && isViewOnly
	const disabled = disabledProp || isBlocked
	const buttonTypes = {
		'Primary':
			`disabled:bg-charcoal-300 disabled:text-charcoal-600 disabled:border-charcoal-300 text-charcoal-900 bg-algae-500 hover:bg-algae-600 border-2 border-algae-500 hover:border-algae-600 focus-visible:bg-algae-500 focus-visible:border-algae-500 ${FOCUS_VISIBLE_RING_OFFSET}`,
		'Secondary':
			`disabled:bg-charcoal-300 disabled:text-charcoal-600 disabled:border-charcoal-300 bg-charcoal-100 hover:bg-charcoal-300 border-2 border-charcoal-100 hover:border-charcoal-300 focus-visible:bg-charcoal-100 focus-visible:border-charcoal-100 ${FOCUS_VISIBLE_RING_OFFSET}`,
		'White':
			`disabled:bg-charcoal-300 disabled:text-charcoal-600 disabled:border-charcoal-300 bg-white hover:bg-algae-050 border-2 border-white hover:border-algae-050 focus-visible:bg-white focus-visible:border-white ${FOCUS_VISIBLE_RING}`,
		'On dark':
			`disabled:bg-charcoal-300 disabled:text-charcoal-600 disabled:border-charcoal-300 bg-white hover:bg-algae-050 border-2 border-white focus-visible:bg-white focus-visible:border-white ${FOCUS_VISIBLE_RING}`,
		'Green-Secondary':
			`disabled:bg-charcoal-300 disabled:text-charcoal-600 disabled:border-charcoal-300 bg-algae-200 hover:bg-algae-300 border-2 border-algae-200 hover:border-algae-300 focus-visible:bg-algae-200 focus-visible:border-algae-200 ${FOCUS_VISIBLE_RING_OFFSET}`,
		'Outlined':
			`disabled:bg-charcoal-300 disabled:text-charcoal-600 disabled:border-charcoal-300 text-algae-500 hover:text-charcoal-900 border-2 border-algae-500 hover:bg-algae-600 hover:border-algae-600 focus-visible:bg-transparent focus-visible:border-algae-500 ${FOCUS_VISIBLE_RING_OFFSET}`,
		// pl-0 flush-aligns text-bearing Link buttons with surrounding content; omitted for icon-only buttons so the icon stays centred in the focus ring
		'Link': `text-charcoal-100 hover:text-charcoal-050 ${text !== '' ? 'pl-0' : ''} ${FOCUS_VISIBLE_RING}`,
		'Pagination':
			`h-12 min-w-[48px] disabled:bg-transparent disabled:border-transparent disabled:text-charcoal-300 text-algae-200 hover:text-charcoal-900 hover:bg-algae-100 hover:border-algae-100 focus-visible:text-charcoal-900 focus-visible:bg-algae-100 focus-visible:border-algae-100 border-2 border-transparent ${FOCUS_VISIBLE_RING_OFFSET}`,
	}

	const paddingClasses = {
		xs: 'px-2.5 py-1.5 text-xs',
		sm: 'py-1.5 px-3',
		md: 'py-2.5 px-4',
		lg: 'py-3 px-5',
	}

	// equal padding on all sides (matching each size's vertical padding) so an icon-only button renders as a square, not the wider text-button box
	const iconOnlyPaddingClasses = {
		xs: 'p-1.5',
		sm: 'p-1.5',
		md: 'p-2.5',
		lg: 'p-3',
	}

	// Literal classes so Tailwind generates them (`text-${size}` is not detectable, and there is no `text-md`).
	const fontSizeClasses = {
		xs: 'text-xs',
		sm: 'text-sm',
		md: 'text-base',
		lg: 'text-lg',
	}

	const isIconOnly = text === '' && !!icon
	const isFormField = context === 'form-field'
	const isTableRow = isTableRowButton || context === 'table-row'
	// For table row buttons, use unified padding (matching icon-only) so text and icon buttons render at same height
	const usesUnifiedPadding = !isFormField && (isTableRow || isIconOnly)

	const getClassName = (type: string) => {
		const baseClass = 'group inline-flex items-center justify-center type-button '
		let className =
			baseClass +
			`${usesUnifiedPadding ? iconOnlyPaddingClasses[size] : paddingClasses[size]} ${fontSizeClasses[size]} ${
				fullWidth ? 'w-full' : ''
			} `
		type ObjectKey = keyof typeof buttonTypes
		let typeVal = type as ObjectKey
		className = className + buttonTypes[typeVal]
		// For table-row buttons with text, collapse line-height to match icon-only buttons (FontAwesome's line-height:1)
		if (isTableRow && text !== '') {
			className = className + ' leading-none'
		}
		// Fixed field height (border-box) with vertical padding zeroed, so it lines up with adjacent inputs
		if (isFormField) {
			className = className + ` ${FORM_FIELD_HEIGHT_CLASS} !py-0 leading-none`
		}
		if (isRounded) {
			className = className + ' rounded-full'
		} else {
			className = className + ' rounded'
		}
		if (typeof additionalClass !== 'undefined') {
			className = className + ' ' + additionalClass
		}
		return className
	}

	const getIcon = (icon: string) => {
		if (type === 'Outlined') {
			icon = icon + ' text-algae-500 group-hover:text-charcoal-900'
		}
		if (text !== '') {
			if (hasLeadingIcon) {
				icon = icon + ' mr-md'
			}
			if (hasTrailingIcon) {
				icon = icon + ' ml-md'
			}
		}
		return icon
	}

	const children = (
		<>
			{hasLeadingIcon && icon && <span className={getIcon(icon)}></span>}
			{text}
			{accessibilityLabel && <span className='sr-only'> {accessibilityLabel}</span>}
			{isBlocked && <span className='sr-only'> ({VIEW_ONLY_MESSAGE})</span>}
			{hasTrailingIcon && icon && <span className={getIcon(icon)}></span>}
		</>
	)

	return isLink && !isBlocked ? (
		// NavLink version of button for use with router
		<NavLink
			ref={ref as React.Ref<HTMLAnchorElement>}
			id={id}
			to={url}
			state={routeState}
			className={getClassName(type)}
			onClick={(event) => {
				onClick && onClick(event)
			}}
		>
			{children}
		</NavLink>
	) : (
		// Normal button element
		<button
			ref={ref as React.Ref<HTMLButtonElement>}
			id={id}
			data-testid='component-btn'
			title={isBlocked ? VIEW_ONLY_MESSAGE : undefined}
			onClick={(event) => {
				if (!disabled) {
					onClick && onClick(event)
				}
			}}
			className={getClassName(type)}
			disabled={disabled ? true : false}
			aria-controls={ariaControls}
			aria-expanded={ariaExpanded}
			type={buttonType}
		>
			{children}
		</button>
	)
})
export default Button
